import React, { useState, useEffect, useMemo } from "react";
import { XMarkIcon, PlusIcon, TrashIcon, ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useConfirm } from "@/ui-components/ConfirmDialog";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { useTagSearch, getTagsByIds } from "@/shared/api/newsquawk-headlines";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { getWatchlistType } from "@/widget-components/firehose-widgets/shared/hooks/searchUtils";
import { validateKeywordsSize, MAX_KEYWORDS_COUNT, MAX_KEYWORDS_TOTAL_CHARS } from "@/pages/PortfolioBuilder/keywordLimits";
import {
  containsKeywordDelimiter,
  containsTickerDelimiter,
  parseCsvKeywords,
  parseCsvTickers,
  parsePastedKeywords,
  parsePastedTickers,
} from "@/pages/PortfolioBuilder/parsePastedList";
import type { Portfolio } from "@/types/Portfolio";
import type { LibraryItem } from "./LibraryBrowserContext";
import { useEnsureWatchlistAlerts } from "@/shared/hooks/useEnsureWatchlistAlerts";
import toast from "react-hot-toast";

interface DetailPaneEditorProps {
  item: LibraryItem;
  onCancel: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

type Category = { title: string; id: number | null };

export default function DetailPaneEditor({ item, onCancel, onSaved, onDeleted }: DetailPaneEditorProps) {
  const meta = item.metadata ?? {};
  const itemType = meta.itemType as string | undefined;

  if (itemType === "tag") {
    return <TagWatchlistEditor item={item} onCancel={onCancel} onSaved={onSaved} onDeleted={onDeleted} />;
  }

  return <WatchlistEditor item={item} onCancel={onCancel} onSaved={onSaved} onDeleted={onDeleted} />;
}

// ═══════════════════════════════════════════════════════════════════════
// Watchlist Editor (ticker / keyword)
// ═══════════════════════════════════════════════════════════════════════

function WatchlistEditor({ item, onCancel, onSaved, onDeleted }: DetailPaneEditorProps) {
  const confirm = useConfirm();
  const { ensurePopup, ensureSound } = useEnsureWatchlistAlerts();
  const { updateMutation, deleteMutation, portfoliosQuery } = usePortfoliosCRUD();
  const portfolios = portfoliosQuery.data || [];
  const meta = item.metadata ?? {};
  const portfolioId = meta.portfolioId as number;
  const portfolio = portfolios.find((p) => p.id === portfolioId);

  const [name, setName] = useState(portfolio?.name ?? "");
  const [tickers, setTickers] = useState<string[]>(portfolio?.tickers ?? []);
  const [keywords, setKeywords] = useState<string[]>(portfolio?.keywords ?? []);
  const [color, setColor] = useState(portfolio?.color ?? "#FF0000");
  const [priority, setPriority] = useState(portfolio?.priority ?? 1);
  const [notifications, setNotifications] = useState(portfolio?.notifications ?? { email: false, popup: false, sound: false });
  const [tickerInput, setTickerInput] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const watchlistType = portfolio ? getWatchlistType(portfolio) : "ticker-only";
  const isLegacyMixed = watchlistType === "mixed";
  const showTickers = watchlistType !== "keyword-only";
  const showKeywords = watchlistType !== "ticker-only";
  const keywordsSize = validateKeywordsSize(keywords);

  // Reset form when portfolio changes
  useEffect(() => {
    if (!portfolio) return;
    setName(portfolio.name);
    setTickers(portfolio.tickers);
    setKeywords(portfolio.keywords ?? []);
    setColor(portfolio.color);
    setPriority(portfolio.priority);
    setNotifications(portfolio.notifications);
  }, [portfolio]);

  const handleAddTicker = () => {
    const val = tickerInput.trim().toUpperCase();
    if (val && !tickers.includes(val)) {
      setTickers([...tickers, val]);
      setTickerInput("");
      setErrors((e) => ({ ...e, tickers: "" }));
    }
  };

  const handleAddTickersBulk = (values: string[]) => {
    if (values.length === 0) return;
    setTickers((prev) => {
      const seen = new Set(prev);
      const next = [...prev];
      for (const v of values) {
        if (!seen.has(v)) {
          seen.add(v);
          next.push(v);
        }
      }
      return next;
    });
    setTickerInput("");
    setErrors((e) => ({ ...e, tickers: "" }));
  };

  const handleAddKeyword = () => {
    const val = keywordInput.trim();
    if (val && !keywords.includes(val)) {
      setKeywords([...keywords, val]);
      setKeywordInput("");
    }
  };

  const handleAddKeywordsBulk = (values: string[]) => {
    if (values.length === 0) return;
    setKeywords((prev) => {
      const seen = new Set(prev);
      const next = [...prev];
      for (const v of values) {
        if (!seen.has(v)) {
          seen.add(v);
          next.push(v);
        }
      }
      return next;
    });
    setKeywordInput("");
  };

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      handleAddTickersBulk(parseCsvTickers(text));
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleKeywordCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      handleAddKeywordsBulk(parseCsvKeywords(text));
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleSubmit = async () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Name is required";
    if (showTickers && !showKeywords && tickers.length === 0) errs.tickers = "At least one ticker is required";
    if (!showTickers && showKeywords && keywords.length === 0) errs.keywords = "At least one keyword is required";
    if (showTickers && showKeywords && tickers.length === 0 && keywords.length === 0) errs.tickers = "At least one ticker or keyword is required";
    if (keywords.length > 0 && !keywordsSize.ok) errs.keywords = keywordsSize.error ?? "Keyword limit exceeded";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    if (!portfolio) return;
    setIsSubmitting(true);
    try {
      await updateMutation.mutateAsync({
        ...portfolio,
        name: name.trim(),
        tickers,
        keywords,
        color,
        priority,
        notifications,
      });
      toast.success("Watchlist updated");
      onSaved();
    } catch {
      toast.error("Failed to update watchlist");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!portfolio) return;
    const ok = await confirm({ title: "Delete Watchlist", message: "This watchlist will be permanently deleted. This action cannot be undone.", confirmLabel: "Delete", variant: "danger" });
    if (!ok) return;
    try {
      await deleteMutation.mutateAsync(String(portfolio.id));
      toast.success("Watchlist deleted");
      onDeleted();
    } catch {
      toast.error("Failed to delete watchlist");
    }
  };

  return (
    <div className="h-full flex flex-col border-l border-gray-700/50 bg-gray-900/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700/30">
        <h3 className="text-sm font-semibold text-gray-100">Edit Watchlist</h3>
        <button onClick={handleDelete} className="text-xs text-red-400/70 hover:text-red-400 transition-colors">
          Delete
        </button>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent px-4 py-4 space-y-4">
        {/* Name */}
        <div>
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Name</label>
          <input
            value={name}
            onChange={(e) => { setName(e.target.value); setErrors((er) => ({ ...er, name: "" })); }}
            className="mt-1 w-full px-3 py-2 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
          />
          {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
        </div>

        {/* Tickers */}
        {showTickers && (
          <div>
            <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Tickers</label>
            <div className="mt-1 flex gap-1.5">
              <input
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTicker())}
                onPaste={(e) => {
                  const text = e.clipboardData.getData("text");
                  if (!containsTickerDelimiter(text)) return;
                  e.preventDefault();
                  handleAddTickersBulk(parsePastedTickers(text));
                }}
                placeholder="Add ticker..."
                className="flex-1 px-3 py-1.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-600"
              />
              <button onClick={handleAddTicker} className="px-2 py-1.5 text-xs rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors">
                <PlusIcon className="size-3.5" />
              </button>
            </div>
            {/* CSV upload */}
            <label className="mt-1.5 flex items-center gap-1.5 text-xs text-squawk-blue cursor-pointer hover:text-squawk-blue/80 transition-colors">
              <input type="file" accept=".csv" onChange={handleCsvUpload} className="sr-only" />
              Upload CSV
            </label>
            {/* Chips */}
            {tickers.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1 max-h-32 overflow-y-auto scrollbar-thin">
                {tickers.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-blue-900/20 text-blue-300 border border-blue-800/30 text-xs">
                    {t}
                    <button onClick={() => setTickers(tickers.filter((x) => x !== t))} className="p-0.5 text-blue-400 hover:text-blue-200">
                      <XMarkIcon className="size-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {errors.tickers && <p className="text-xs text-red-400 mt-1">{errors.tickers}</p>}
          </div>
        )}

        {/* Keywords */}
        {showKeywords && (
          <div>
            <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Keywords</label>
            <div className="mt-1 flex gap-1.5">
              <input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddKeyword())}
                onPaste={(e) => {
                  const text = e.clipboardData.getData("text");
                  if (!containsKeywordDelimiter(text)) return;
                  e.preventDefault();
                  handleAddKeywordsBulk(parsePastedKeywords(text));
                }}
                placeholder="Add keyword..."
                className="flex-1 px-3 py-1.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-600"
              />
              <button onClick={handleAddKeyword} className="px-2 py-1.5 text-xs rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors">
                <PlusIcon className="size-3.5" />
              </button>
            </div>
            {/* CSV upload */}
            <label className="mt-1.5 flex items-center gap-1.5 text-xs text-squawk-blue cursor-pointer hover:text-squawk-blue/80 transition-colors">
              <input type="file" accept=".csv" onChange={handleKeywordCsvUpload} className="sr-only" />
              Upload CSV
            </label>
            {keywords.length > 0 && (
              <>
                <div className="mt-2 flex flex-wrap gap-1 max-h-32 overflow-y-auto scrollbar-thin">
                  {keywords.map((k) => (
                    <span key={k} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-purple-900/20 text-purple-300 border border-purple-800/30 text-xs">
                      {k}
                      <button onClick={() => setKeywords(keywords.filter((x) => x !== k))} className="p-0.5 text-purple-400 hover:text-purple-200">
                        <XMarkIcon className="size-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className={clsx("mt-1 text-xs", keywordsSize.ok ? "text-gray-500" : "text-red-400")}>
                  {keywordsSize.count}/{MAX_KEYWORDS_COUNT} keywords · {keywordsSize.totalChars}/{MAX_KEYWORDS_TOTAL_CHARS} chars
                </div>
              </>
            )}
            {errors.keywords && <p className="text-xs text-red-400 mt-1">{errors.keywords}</p>}
          </div>
        )}

        {/* Color */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Color</span>
          <label className="flex items-center gap-2 cursor-pointer px-2.5 py-1.5 rounded border border-gray-700 bg-gray-800 hover:border-squawk-blue transition-colors">
            <span className="size-4 rounded border border-gray-600" style={{ backgroundColor: color }} />
            <span className="text-xs text-gray-300 font-mono">{color}</span>
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="sr-only" />
          </label>
        </div>

        {/* Priority */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Priority</span>
            <p className="text-xs text-gray-600">Lower wins on overlap</p>
          </div>
          <select
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value))}
            className="w-16 px-2 py-1.5 text-sm rounded border border-gray-700 bg-gray-800 text-gray-100 focus:outline-none focus:border-squawk-blue"
          >
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        {/* Notifications */}
        <div>
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Notifications</span>
          <div className="mt-2 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={notifications.email} onChange={(e) => setNotifications({ ...notifications, email: e.target.checked })}
                className="size-3.5 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0" />
              <span className="text-sm text-gray-300">Email</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={notifications.sound} onChange={(e) => {
                if (e.target.checked) ensureSound();
                setNotifications({ ...notifications, sound: e.target.checked });
              }}
                className="size-3.5 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0" />
              <span className="text-sm text-gray-300">Sound</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={notifications.popup} onChange={(e) => {
                if (e.target.checked) ensurePopup();
                setNotifications({ ...notifications, popup: e.target.checked });
              }}
                className="size-3.5 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0" />
              <span className="text-sm text-gray-300">Pop-Up</span>
            </label>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-700/30 space-y-2">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full px-3 py-2.5 text-sm font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors disabled:opacity-50"
        >
          {isSubmitting ? "Saving..." : "Save Changes"}
        </button>
        <button
          onClick={onCancel}
          className="w-full px-3 py-2 text-sm font-medium rounded bg-gray-700/50 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-100 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Tag Watchlist Editor
// ═══════════════════════════════════════════════════════════════════════

function TagWatchlistEditor({ item, onCancel, onSaved, onDeleted }: DetailPaneEditorProps) {
  const confirm = useConfirm();
  const { createMutation, updateMutation, deleteMutation } = useTagsWatchlistsCRUD();
  const { search: searchTags, tags: searchedTags, isLoading: tagSearchLoading } = useTagSearch();

  const meta = item.metadata ?? {};
  const tagWatchlistId = meta.tagWatchlistId as number;

  const [name, setName] = useState("");
  const [selectedTags, setSelectedTags] = useState<Category[]>([]);
  const [notifications, setNotifications] = useState({ email: false, popup: false, sound: false });
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const [tagSearchedQuery, setTagSearchedQuery] = useState("");
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set(menuCategories.map((c) => c.title)));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load existing watchlist data
  useEffect(() => {
    const tags = (meta.tags as { id: string; name: string }[]) ?? [];
    setName(item.label);
    setSelectedTags(tags.map((t) => ({ title: t.name, id: Number(t.id) })));
    const notif = meta.notifications as { email?: boolean; popup?: boolean; sound?: boolean } | undefined;
    setNotifications({
      email: notif?.email ?? false,
      popup: notif?.popup ?? false,
      sound: notif?.sound ?? false,
    });
  }, [item.id]);

  const toggleTag = (tag: Category) => {
    setSelectedTags((prev) => {
      const exists = prev.some((t) => t.id === tag.id);
      return exists ? prev.filter((t) => t.id !== tag.id) : [...prev, tag];
    });
    setErrors((e) => ({ ...e, tags: "" }));
  };

  const handleSubmit = async () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Name is required";
    if (selectedTags.length === 0) errs.tags = "At least one tag is required";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setIsSubmitting(true);
    try {
      await updateMutation.mutateAsync({
        id: tagWatchlistId,
        data: {
          name: name.trim(),
          tags: selectedTags.map((t) => String(t.id)),
          notifications,
        },
      });
      toast.success("Tag watchlist updated");
      onSaved();
    } catch {
      toast.error("Failed to update tag watchlist");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({ title: "Delete Tag Watchlist", message: "This tag watchlist will be permanently deleted. This action cannot be undone.", confirmLabel: "Delete", variant: "danger" });
    if (!ok) return;
    try {
      await deleteMutation.mutateAsync(tagWatchlistId);
      toast.success("Tag watchlist deleted");
      onDeleted();
    } catch {
      toast.error("Failed to delete tag watchlist");
    }
  };

  return (
    <div className="h-full flex flex-col border-l border-gray-700/50 bg-gray-900/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700/30">
        <h3 className="text-sm font-semibold text-gray-100">Edit Tag Watchlist</h3>
        <button onClick={handleDelete} className="text-xs text-red-400/70 hover:text-red-400 transition-colors">
          Delete
        </button>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent px-4 py-4 space-y-4">
        {/* Name */}
        <div>
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Name</label>
          <input
            value={name}
            onChange={(e) => { setName(e.target.value); setErrors((er) => ({ ...er, name: "" })); }}
            className="mt-1 w-full px-3 py-2 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
          />
          {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
        </div>

        {/* Selected tags */}
        {selectedTags.length > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Selected Tags ({selectedTags.length})</label>
            <div className="mt-1 flex flex-wrap gap-1 max-h-24 overflow-y-auto scrollbar-thin">
              {selectedTags.map((tag) => (
                <span key={tag.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-green-900/20 text-green-300 border border-green-800/30 text-xs">
                  {tag.title}
                  <button onClick={() => toggleTag(tag)} className="p-0.5 text-green-400 hover:text-green-200">
                    <XMarkIcon className="size-2.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
        {errors.tags && <p className="text-xs text-red-400">{errors.tags}</p>}

        {/* Tag search */}
        <div>
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Search Tags</label>
          <div className="mt-1 flex gap-1.5">
            <input
              value={tagSearchQuery}
              onChange={(e) => setTagSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && tagSearchQuery.trim()) {
                  e.preventDefault();
                  setTagSearchedQuery(tagSearchQuery);
                  searchTags(tagSearchQuery);
                }
              }}
              placeholder="Search for tags..."
              className="flex-1 px-3 py-1.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-600"
            />
            <button
              onClick={() => { if (tagSearchQuery.trim()) { setTagSearchedQuery(tagSearchQuery); searchTags(tagSearchQuery); } }}
              disabled={!tagSearchQuery.trim() || tagSearchLoading}
              className="px-3 py-1.5 text-xs rounded border border-squawk-blue bg-squawk-blue/20 text-squawk-blue hover:bg-squawk-blue/30 transition-colors disabled:opacity-50"
            >
              {tagSearchLoading ? "..." : "Search"}
            </button>
          </div>
          {tagSearchedQuery && tagSearchQuery === tagSearchedQuery && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {searchedTags.length === 0 && !tagSearchLoading && (
                <span className="text-xs text-gray-500">No tags found</span>
              )}
              {searchedTags.map((tag) => {
                const isSelected = selectedTags.some((t) => t.id === tag.tag_id);
                return (
                  <button
                    key={tag.tag_id}
                    onClick={() => toggleTag({ title: tag.name, id: tag.tag_id })}
                    className={clsx(
                      "px-2 py-1 text-xs rounded border transition-colors",
                      isSelected
                        ? "bg-squawk-blue/20 text-squawk-blue border-squawk-blue"
                        : "bg-gray-700 text-gray-400 border-gray-600 hover:border-gray-500"
                    )}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Browse by category */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Browse by Category</label>
            <button
              onClick={() => {
                if (collapsedSections.size === menuCategories.length) setCollapsedSections(new Set());
                else setCollapsedSections(new Set(menuCategories.map((c) => c.title)));
              }}
              className="text-2xs px-1.5 py-0.5 rounded border border-gray-700 text-squawk-blue hover:border-squawk-blue/50"
            >
              {collapsedSections.size === menuCategories.length ? "Expand" : "Collapse"}
            </button>
          </div>
          <div className="space-y-1">
            {menuCategories.map((category) => {
              const isCollapsed = collapsedSections.has(category.title);
              return (
                <div key={category.title} className="border border-gray-700/50 rounded overflow-hidden">
                  <button
                    onClick={() => {
                      setCollapsedSections((prev) => {
                        const next = new Set(prev);
                        if (next.has(category.title)) next.delete(category.title);
                        else next.add(category.title);
                        return next;
                      });
                    }}
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-400 hover:text-gray-200 bg-gray-800/30 hover:bg-gray-800/50 transition-colors focus:outline-none"
                  >
                    {isCollapsed ? <ChevronRightIcon className="size-3" /> : <ChevronDownIcon className="size-3" />}
                    {category.title}
                  </button>
                  {!isCollapsed && (
                    <div className="p-2 grid grid-cols-2 gap-1 bg-gray-900/20">
                      {category.items.map((catItem) => {
                        const isSelected = selectedTags.some((t) => t.id === catItem.id);
                        return (
                          <label key={catItem.id} className={clsx(
                            "flex items-center gap-1.5 px-1.5 py-1 rounded cursor-pointer text-xs transition-colors",
                            isSelected ? "bg-squawk-blue/10 text-squawk-blue" : "text-gray-400 hover:bg-gray-800"
                          )}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleTag({ title: catItem.title, id: catItem.id })}
                              className="size-3 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="truncate">{catItem.title}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Notifications */}
        <div>
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Notifications</span>
          <div className="mt-2 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={notifications.email} onChange={(e) => setNotifications({ ...notifications, email: e.target.checked })}
                className="size-3.5 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0" />
              <span className="text-sm text-gray-300">Email</span>
            </label>
            <div className="opacity-40 pointer-events-none space-y-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" disabled className="size-3.5 rounded border-gray-600 bg-gray-800 focus:ring-0" />
                <span className="text-sm text-gray-300">Sound (Coming Soon)</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" disabled className="size-3.5 rounded border-gray-600 bg-gray-800 focus:ring-0" />
                <span className="text-sm text-gray-300">Pop-Up (Coming Soon)</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-700/30 space-y-2">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full px-3 py-2.5 text-sm font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors disabled:opacity-50"
        >
          {isSubmitting ? "Saving..." : "Save Changes"}
        </button>
        <button
          onClick={onCancel}
          className="w-full px-3 py-2 text-sm font-medium rounded bg-gray-700/50 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-100 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
